import {
  clampToImage,
  dragMove,
  finishFreehandStroke,
  hoverPoint,
  normalizePoint,
  type PointerKind,
  PREVIEW_MS,
  type Pt,
  resolveDragStart,
  resolveLongPress,
  resolveTap,
  setTransform,
  shouldRecordStrokePoint,
  type Transform,
  type ViewGeometry,
  wheelTransform,
} from '@better-lights/core';
import type { DepthMap } from '../../renderer/types.ts';
import { dispatch, projectState } from '../../state/documentStore.ts';
import { toast } from '../../state/feedback.ts';
import { useSettings } from '../../state/settingsStore.ts';
import { setUi } from '../../state/uiStore.ts';
import { edgeFieldFor } from './edgeField.ts';
import { hitInput, mayEdit, pointerKind } from './hit.ts';
import type { Live } from './live.ts';
import { flatPoints } from './overlay.ts';

/** Gesture thresholds (docs/spec/06 §4.4). */
const PAN_MIN = 4;
const TAP_MAX_MS = 400;
const TAP_MAX_DIST = 10;
const LONG_PRESS_MS = 450;
const LONG_PRESS_DIST = 8;

export interface ControllerHost {
  live: Live;
  invalidate(): void;
  geometry(): ViewGeometry | null;
  photo(): ImageBitmap | null;
  depth(): DepthMap | null;
}

interface Ptr {
  id: number;
  kind: PointerKind;
  x: number;
  y: number;
  x0: number;
  y0: number;
  t0: number;
}

type Mode =
  | { kind: 'idle' }
  | { kind: 'pending'; id: number }
  | { kind: 'drag'; id: number }
  | { kind: 'stroke'; id: number }
  | { kind: 'pan'; id: number; start: Transform; x0: number; y0: number }
  | { kind: 'pinch'; a: number; b: number; start: Transform; d0: number; cx0: number; cy0: number }
  | { kind: 'consumed'; id: number };

/**
 * Pointer Events on the canvas stage. Pen edits while touch pans/zooms (per the Pencil mode
 * rules), mouse follows the tool (space-drag / middle-drag pan), wheel zooms, ctrl+wheel is
 * trackpad pinch, two-finger scroll pans. Writes tier-3 live state and dispatches document
 * actions; never triggers React renders per move.
 */
export class CanvasController {
  private readonly pointers = new Map<number, Ptr>();
  private mode: Mode = { kind: 'idle' };
  private longPress: ReturnType<typeof setTimeout> | undefined;
  private rect: DOMRect;
  private spaceDown = false;
  private readonly off: (() => void)[] = [];

  constructor(
    private readonly el: HTMLElement,
    private readonly host: ControllerHost,
  ) {
    this.rect = el.getBoundingClientRect();
    const on = <K extends keyof HTMLElementEventMap>(
      target: HTMLElement | Window,
      type: K | string,
      fn: (e: never) => void,
      opts?: AddEventListenerOptions,
    ) => {
      target.addEventListener(type, fn as EventListener, opts);
      this.off.push(() => target.removeEventListener(type, fn as EventListener, opts));
    };
    on(el, 'pointerdown', (e: PointerEvent) => this.down(e));
    on(el, 'pointermove', (e: PointerEvent) => this.move(e));
    on(el, 'pointerup', (e: PointerEvent) => this.up(e, false));
    on(el, 'pointercancel', (e: PointerEvent) => this.up(e, true));
    on(el, 'pointerleave', (e: PointerEvent) => this.leave(e));
    on(el, 'wheel', (e: WheelEvent) => this.wheel(e), { passive: false });
    on(el, 'gesturestart', (e: Event) => e.preventDefault(), { passive: false });
    on(el, 'gesturechange', (e: Event) => e.preventDefault(), { passive: false });
    on(el, 'contextmenu', (e: Event) => e.preventDefault());
    on(window, 'keydown', (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isTyping(e)) {
        this.spaceDown = true;
        this.updateCursor();
      }
    });
    on(window, 'keyup', (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        this.spaceDown = false;
        this.updateCursor();
      }
    });
    this.updateCursor();
  }

  dispose(): void {
    clearTimeout(this.longPress);
    for (const f of this.off) f();
  }

  /** Tool / state changed: refresh the cursor. */
  updateCursor(): void {
    const tool = projectState().tool;
    this.el.style.cursor =
      this.mode.kind === 'pan' || this.mode.kind === 'pinch'
        ? 'grabbing'
        : this.spaceDown
          ? 'grab'
          : tool === 'select'
            ? 'default'
            : 'crosshair';
  }

  /* ------------------------------------------------------------------ events */

  private pos(e: PointerEvent | WheelEvent): [number, number] {
    return [e.clientX - this.rect.left, e.clientY - this.rect.top];
  }

  private down(e: PointerEvent): void {
    if (e.pointerType === 'mouse' && e.button === 2) return;
    this.rect = this.el.getBoundingClientRect();
    const [x, y] = this.pos(e);
    const kind = pointerKind(e);
    const p: Ptr = { id: e.pointerId, kind, x, y, x0: x, y0: y, t0: performance.now() };
    if (kind === 'stylus') mayEdit(kind);
    this.host.live.fitAnim = null;
    this.host.live.hover = kind === 'touch' ? null : this.host.live.hover;
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      // synthetic events in tests may not be capturable
    }
    if (e.pointerType === 'mouse') e.preventDefault();

    if (kind === 'touch') {
      const other = [...this.pointers.values()].find((q) => q.kind === 'touch');
      if (other) {
        const m = this.mode;
        if (
          m.kind === 'drag' ||
          (m.kind !== 'idle' && m.kind !== 'pinch' && this.pointers.get(m.id)?.kind === 'stylus')
        )
          return;
        if (m.kind === 'pinch') return;
        if (m.kind === 'stroke') this.host.live.stroke = null; // a second finger cancels the stroke
        this.pointers.set(p.id, p);
        this.startPinch(other, p);
        return;
      }
    }
    if (this.mode.kind !== 'idle') return; // another pointer owns the gesture
    this.pointers.set(p.id, p);
    if ((e.pointerType === 'mouse' && e.button === 1) || (e.pointerType === 'mouse' && this.spaceDown)) {
      this.mode = { kind: 'pan', id: p.id, start: { ...this.host.live.transform }, x0: x, y0: y };
      this.updateCursor();
      return;
    }
    this.mode = { kind: 'pending', id: p.id };
    clearTimeout(this.longPress);
    this.longPress = setTimeout(() => this.onLongPress(p.id), LONG_PRESS_MS);
  }

  private move(e: PointerEvent): void {
    const [x, y] = this.pos(e);
    const p = this.pointers.get(e.pointerId);
    const m = this.mode;
    if (!p) {
      if (e.buttons === 0 && (e.pointerType === 'mouse' || e.pointerType === 'pen')) {
        if (e.pointerType === 'pen') mayEdit('stylus');
        this.hover(x, y);
      }
      return;
    }
    p.x = x;
    p.y = y;
    const live = this.host.live;
    switch (m.kind) {
      case 'pending': {
        if (m.id !== p.id) return;
        const d = Math.hypot(x - p.x0, y - p.y0);
        if (d > LONG_PRESS_DIST) clearTimeout(this.longPress);
        if (d >= PAN_MIN) this.beginGesture(p);
        return;
      }
      case 'drag':
        if (m.id === p.id) this.dragTo(x, y, false);
        return;
      case 'stroke': {
        if (m.id !== p.id || !live.stroke) return;
        const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
        for (const ce of events.length ? events : [e]) {
          const [cx, cy] = this.pos(ce);
          this.strokeTo(cx, cy);
        }
        this.host.invalidate();
        return;
      }
      case 'pan': {
        if (m.id !== p.id) return;
        const g = this.host.geometry();
        if (!g) return;
        live.transform = setTransform(m.start.s, m.start.tx + x - m.x0, m.start.ty + y - m.y0, g);
        this.host.invalidate();
        return;
      }
      case 'pinch':
        if (p.id === m.a || p.id === m.b) this.pinchUpdate();
        return;
      default:
        return;
    }
  }

  private up(e: PointerEvent, cancelled: boolean): void {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    const m = this.mode;
    clearTimeout(this.longPress);
    const [x, y] = this.pos(e);
    p.x = x;
    p.y = y;
    this.pointers.delete(p.id);
    switch (m.kind) {
      case 'pending':
        if (m.id === p.id) {
          this.mode = { kind: 'idle' };
          const elapsed = performance.now() - p.t0;
          if (!cancelled && elapsed <= TAP_MAX_MS && Math.hypot(x - p.x0, y - p.y0) <= TAP_MAX_DIST) this.tap(p);
        }
        break;
      case 'drag':
        if (m.id === p.id) {
          this.dragTo(x, y, true);
          this.mode = { kind: 'idle' };
        }
        break;
      case 'stroke':
        if (m.id === p.id) {
          this.mode = { kind: 'idle' };
          if (cancelled) this.host.live.stroke = null;
          else this.finishStroke();
        }
        break;
      case 'pan':
        if (m.id === p.id) this.mode = { kind: 'idle' };
        break;
      case 'pinch':
        if (p.id === m.a || p.id === m.b) {
          const rest = this.pointers.get(p.id === m.a ? m.b : m.a);
          this.mode = rest
            ? { kind: 'pan', id: rest.id, start: { ...this.host.live.transform }, x0: rest.x, y0: rest.y }
            : { kind: 'idle' };
        }
        break;
      case 'consumed':
        if (m.id === p.id) this.mode = { kind: 'idle' };
        break;
      default:
        break;
    }
    this.updateCursor();
    this.host.invalidate();
  }

  private leave(e: PointerEvent): void {
    if (this.pointers.has(e.pointerId)) return;
    if (this.host.live.hover) {
      this.host.live.hover = null;
      this.host.invalidate();
    }
  }

  private wheel(e: WheelEvent): void {
    e.preventDefault();
    const g = this.host.geometry();
    if (!g) return;
    this.rect = this.el.getBoundingClientRect();
    const [x, y] = this.pos(e);
    this.host.live.fitAnim = null;
    this.host.live.transform = wheelTransform(
      this.host.live.transform,
      { deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode, ctrlKey: e.ctrlKey, x, y },
      g,
    );
    this.host.invalidate();
  }

  /* ---------------------------------------------------------------- gestures */

  private tap(p: Ptr): void {
    const editing = mayEdit(p.kind);
    const input = hitInput(this.host.live, p.x, p.y, p.kind, editing);
    if (!input) return;
    const s = projectState();
    const r = resolveTap({ ...input, tool: s.tool });
    switch (r.type) {
      case 'insertPoint':
        dispatch({ type: 'insertPoint', runId: s.runs[r.run].id, index: r.index, p: r.p });
        break;
      case 'addPoint':
        if (!s.activeRunId) setUi({ expanded: false }); // starting a run collapses the sheet
        dispatch({ type: 'addPoint', p: r.p });
        break;
      case 'finishRun':
        dispatch({ type: 'finishRun' });
        break;
      case 'selectRun':
        dispatch({ type: 'selectRun', runId: r.run >= 0 ? s.runs[r.run].id : null });
        break;
      default:
        break;
    }
    this.hover(p.x, p.y);
  }

  private onLongPress(id: number): void {
    const p = this.pointers.get(id);
    if (!p || this.mode.kind !== 'pending' || this.mode.id !== id) return;
    if (Math.hypot(p.x - p.x0, p.y - p.y0) > LONG_PRESS_DIST) return;
    const input = hitInput(this.host.live, p.x0, p.y0, p.kind, mayEdit(p.kind));
    if (!input) return;
    const r = resolveLongPress(input);
    if (r.type !== 'removePoint') return;
    dispatch({ type: 'removePoint', runId: projectState().runs[r.run].id, index: r.index });
    this.mode = { kind: 'consumed', id };
    this.host.invalidate();
  }

  private beginGesture(p: Ptr): void {
    clearTimeout(this.longPress);
    const live = this.host.live;
    const editing = mayEdit(p.kind);
    const s = projectState();
    if (!s.photo) return;
    const W = s.photo.width;
    const H = s.photo.height;
    const t = live.transform;
    if (s.tool === 'freehand' && editing) {
      const a = clampToImage((p.x0 - t.tx) / t.s, (p.y0 - t.ty) / t.s, W, H);
      const b = clampToImage((p.x - t.tx) / t.s, (p.y - t.ty) / t.s, W, H);
      live.stroke = [
        { x: a[0], y: a[1] },
        { x: b[0], y: b[1] },
      ];
      live.hover = null;
      this.mode = { kind: 'stroke', id: p.id };
      this.host.invalidate();
      return;
    }
    const input = editing ? hitInput(live, p.x0, p.y0, p.kind, true) : null;
    const ds = input ? resolveDragStart(input) : ({ type: 'view' } as const);
    if (ds.type === 'vertex') {
      const run = s.runs[ds.run];
      if (ds.insert)
        dispatch({ type: 'insertPoint', runId: run.id, index: ds.index, p: normalizePoint(ds.x, ds.y, W, H) });
      else dispatch({ type: 'checkpoint' });
      const focusId = s.activeRunId ?? s.selectedRunId;
      if (focusId !== run.id) dispatch({ type: 'selectRun', runId: run.id });
      live.drag = {
        pointerId: p.id,
        run: ds.run,
        runId: run.id,
        index: ds.index,
        live: ds.live,
        snapped: false,
        touch: p.kind === 'touch',
        lastPush: performance.now(),
      };
      live.hover = null;
      live.loupeAt = p.kind === 'touch' ? { sx: p.x, sy: p.y } : null;
      this.mode = { kind: 'drag', id: p.id };
      this.dragTo(p.x, p.y, false);
      return;
    }
    this.mode = { kind: 'pan', id: p.id, start: { ...t }, x0: p.x0, y0: p.y0 };
    this.updateCursor();
    const g = this.host.geometry();
    if (g) live.transform = setTransform(t.s, t.tx + p.x - p.x0, t.ty + p.y - p.y0, g);
    this.host.invalidate();
  }

  private dragTo(x: number, y: number, commit: boolean): void {
    const live = this.host.live;
    const d = live.drag;
    const s = projectState();
    if (!d || !s.photo) return;
    const W = s.photo.width;
    const H = s.photo.height;
    const runs = s.runs.map((r) => flatPoints(r, W, H));
    const r = dragMove(runs, d.run, d.index, d.live, x, y, live.transform, W, H);
    d.live[d.index * 2] = r.x;
    d.live[d.index * 2 + 1] = r.y;
    d.snapped = r.snapped;
    if (d.touch) live.loupeAt = { sx: x, sy: y };
    const now = performance.now();
    if (commit || now - d.lastPush >= PREVIEW_MS) {
      d.lastPush = now;
      dispatch({ type: 'movePoint', runId: d.runId, index: d.index, p: normalizePoint(r.x, r.y, W, H) });
    }
    if (commit) {
      live.drag = null;
      live.loupeAt = null;
    }
    this.host.invalidate();
  }

  private strokeTo(x: number, y: number): void {
    const live = this.host.live;
    const s = projectState();
    if (!live.stroke || !s.photo) return;
    const t = live.transform;
    const [ix, iy] = clampToImage((x - t.tx) / t.s, (y - t.ty) / t.s, s.photo.width, s.photo.height);
    const next: Pt = { x: ix, y: iy };
    if (shouldRecordStrokePoint(live.stroke[live.stroke.length - 1], next, t.s)) live.stroke.push(next);
  }

  private finishStroke(): void {
    const live = this.host.live;
    const stroke = live.stroke;
    live.stroke = null;
    const s = projectState();
    const photo = this.host.photo();
    if (!stroke || !s.photo) return;
    const W = s.photo.width;
    const H = s.photo.height;
    const snapOn = useSettings.getState().freehandSnap;
    let field = null;
    if (snapOn && photo) {
      try {
        field = edgeFieldFor(photo, this.host.depth());
      } catch {
        field = null;
      }
    }
    const runs = s.runs.map((r) => flatPoints(r, W, H));
    const r = finishFreehandStroke(stroke, live.transform.s, live.transform, runs, W, H, field, snapOn);
    if (r.type === 'discard') return;
    dispatch({ type: 'addRun', points: r.points });
    if (r.toast) toast(r.toast);
  }

  private startPinch(a: Ptr, b: Ptr): void {
    clearTimeout(this.longPress);
    this.host.live.hover = null;
    this.mode = {
      kind: 'pinch',
      a: a.id,
      b: b.id,
      start: { ...this.host.live.transform },
      d0: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
      cx0: (a.x + b.x) / 2,
      cy0: (a.y + b.y) / 2,
    };
    this.updateCursor();
  }

  /** Pinch about the focal point captured at pinch start, plus the averaged two-finger pan. */
  private pinchUpdate(): void {
    const m = this.mode;
    if (m.kind !== 'pinch') return;
    const a = this.pointers.get(m.a);
    const b = this.pointers.get(m.b);
    const g = this.host.geometry();
    if (!a || !b || !g) return;
    const k = Math.hypot(b.x - a.x, b.y - a.y) / m.d0;
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;
    const t = m.start;
    this.host.live.transform = setTransform(
      t.s * k,
      m.cx0 - (m.cx0 - t.tx) * k + (cx - m.cx0),
      m.cy0 - (m.cy0 - t.ty) * k + (cy - m.cy0),
      g,
    );
    this.host.invalidate();
  }

  private hover(x: number, y: number): void {
    const live = this.host.live;
    const s = projectState();
    if (!s.photo || s.tool !== 'draw' || live.drag) {
      if (live.hover) {
        live.hover = null;
        this.host.invalidate();
      }
      return;
    }
    const W = s.photo.width;
    const H = s.photo.height;
    const runs = s.runs.map((r) => flatPoints(r, W, H));
    const focusId = s.activeRunId ?? s.selectedRunId;
    const focus = focusId ? s.runs.findIndex((r) => r.id === focusId) : -1;
    const drawing = !!s.activeRunId;
    const liveRun = drawing && focus >= 0 ? runs[focus] : [];
    live.hover = hoverPoint(runs, focus, drawing, liveRun, x, y, live.transform, W, H);
    this.host.invalidate();
  }
}

export function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName);
}
